import { useState, useEffect } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { DEPARTMENTS } from '../utils/constants';
import api from '../services/api';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [tasks, setTasks] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (user && (user.role === 'employee' || user.role === 'manager')) {
      fetchAttendanceStatus();
    }
  }, [user]);

  const fetchAttendanceStatus = async () => {
    try {
      const response = await api.get('/attendance/today');
      setAttendanceStatus(response.data.data);
    } catch (error) {
      // No attendance for today yet
      setAttendanceStatus(null);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      await api.post('/attendance/check-in');
      await fetchAttendanceStatus();
      alert('Checked in successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOutClick = () => {
    setShowTaskModal(true);
  };

  const handleCheckOut = async () => {
    if (!tasks.trim()) {
      alert('Please enter the tasks you completed today');
      return;
    }

    setLoading(true);
    try {
      await api.post('/attendance/check-out', { tasks_completed: tasks });
      await fetchAttendanceStatus();
      setShowTaskModal(false);
      setTasks('');
      alert('Checked out successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check out');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const getNavLinks = () => {
    if (!user) return [];

    const links = [];

    const salesGroup = {
      label: 'Sales',
      icon: 'ti-currency-dollar',
      children: [
        { path: '/sales', label: 'Leads', icon: 'ti-users' },
        { path: '/sales/comments', label: 'Comments', icon: 'ti-message' },
        { path: '/sales/activities', label: 'Activity', icon: 'ti-history' }
      ]
    };

    const staffingGroup = {
      label: 'Staffing Sales',
      icon: 'ti-briefcase',
      children: [
        { path: '/staffing', label: 'Clients', icon: 'ti-building' },
        { path: '/staffing/requirements', label: 'Requirements', icon: 'ti-list-details' },
        { path: '/staffing/candidates', label: 'Candidates', icon: 'ti-user-search' },
        { path: '/staffing/comments', label: 'Comments', icon: 'ti-message' },
        { path: '/staffing/activities', label: 'Activity', icon: 'ti-history' }
      ]
    };

    if (user.role === 'admin') {
      links.push(
        salesGroup,
        { path: '/hr', label: 'HR', icon: 'ti-target' },
        staffingGroup,
        { path: '/admin/employees', label: 'Employees', icon: 'ti-users' },
        { path: '/admin/team-attendance', label: 'All Attendance', icon: 'ti-clipboard-list' },
        { path: '/admin/digest-email', label: 'Email Testing', icon: 'ti-mail' }
      );
    } else {
      // Check if user has departments array
      const userDepts = user.departments || [user.department];

      if (userDepts.includes(DEPARTMENTS.SALES)) {
        links.push(salesGroup);
      }
      if (userDepts.includes(DEPARTMENTS.HR)) {
        links.push({ path: '/hr', label: 'HR Dashboard', icon: 'ti-target' });
        links.push({ path: '/hr/team-attendance', label: 'All Attendance', icon: 'ti-clipboard-list' });
      }
      if (userDepts.includes(DEPARTMENTS.STAFFING_SALES)) {
        links.push(staffingGroup);
      }

      // Add attendance links for all non-admin users
      links.push({ path: '/attendance', label: 'My Attendance', icon: 'ti-calendar-event' });

      // Add team attendance link (backend will authorize based on role, HR department, or reporting manager status)
      links.push({ path: '/team-attendance', label: 'Team Attendance', icon: 'ti-users-group' });
    }

    // Projects is a cross-department feature available to every authenticated user
    links.push({ path: '/projects', label: 'Projects', icon: 'ti-layout-kanban' });

    return links;
  };

  const isActiveRoute = (path) => {
    if (path === '/staffing') {
      return location.pathname === '/staffing' || location.pathname.startsWith('/staffing/clients');
    }
    if (path === '/sales') {
      return location.pathname === '/sales';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.name?.charAt(0).toUpperCase() || '?';
  const showAttendanceActions = user && (user.role === 'employee' || user.role === 'manager');

  return (
    <>
      <div
        className={`overlay ${mobileSidebarOpen ? 'show' : ''}`}
        onClick={() => setMobileSidebarOpen(false)}
      />

      <nav
        className={`navbar bg-white border-bottom fixed-top topbar px-3 ${sidebarCollapsed ? 'full' : ''}`}
      >
        <button
          type="button"
          className="d-none d-lg-inline-flex btn btn-light btn-icon btn-sm"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label="Toggle sidebar"
        >
          <i className="ti ti-layout-sidebar-left-expand" />
        </button>
        <button
          type="button"
          className="btn btn-light btn-icon btn-sm d-lg-none me-2"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open menu"
        >
          <i className="ti ti-layout-sidebar-left-expand" />
        </button>

        <div className="ms-auto d-flex align-items-center gap-2">
          <span className="d-none d-sm-inline text-muted small">{user?.name}</span>
          <span className="avatar avatar-sm avatar-primary rounded-circle">
            <span className="avatar-initials rounded-circle">{initial}</span>
          </span>
        </div>
      </nav>

      <aside
        id="sidebar"
        className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-show' : ''}`}
      >
        <div className="logo-area">
          <span className="icon-shape icon-xs bg-primary text-white rounded-2 fw-semibold flex-shrink-0">
            EM
          </span>
          <span className="nav-text fw-semibold text-truncate">Enterprise Mgmt</span>
        </div>

        <ul className="nav flex-column">
          <li className="px-4 py-2">
            <small className="nav-text text-uppercase text-muted">Main</small>
          </li>
          {getNavLinks().map((link) => (
            link.children ? (
              <li key={link.label}>
                <div className="nav-link nav-group-label">
                  <i className={`ti ${link.icon}`} />
                  <span className="nav-text">{link.label}</span>
                </div>
                <ul className="nav flex-column nav-submenu">
                  {link.children.map((child) => (
                    <li key={child.path}>
                      <NavLink
                        to={child.path}
                        className={`nav-link ${isActiveRoute(child.path) ? 'active' : ''}`}
                        onClick={() => setMobileSidebarOpen(false)}
                      >
                        <i className={`ti ${child.icon}`} />
                        <span className="nav-text">{child.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={link.path}>
                <NavLink
                  to={link.path}
                  className={`nav-link ${isActiveRoute(link.path) ? 'active' : ''}`}
                  onClick={() => setMobileSidebarOpen(false)}
                >
                  <i className={`ti ${link.icon}`} />
                  <span className="nav-text">{link.label}</span>
                </NavLink>
              </li>
            )
          ))}

          <li className="px-4 pt-4 pb-2">
            <small className="nav-text text-uppercase text-muted">Account</small>
          </li>
          <li>
            <NavLink
              to="/profile"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileSidebarOpen(false)}
            >
              <i className="ti ti-user-circle" />
              <span className="nav-text">My Profile</span>
            </NavLink>
          </li>
        </ul>

        <div className="sidebar-footer">
          {showAttendanceActions && (
            <div className="mb-3">
              {!attendanceStatus || attendanceStatus.status === 'checked_out' ? (
                <button
                  onClick={handleCheckIn}
                  disabled={loading}
                  className="btn btn-success btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <i className="ti ti-clock-play nav-text-icon" />
                  <span className="nav-text">{loading ? 'Processing...' : 'Check In'}</span>
                </button>
              ) : attendanceStatus.status === 'checked_in' ? (
                <button
                  onClick={handleCheckOutClick}
                  disabled={loading}
                  className="btn btn-danger btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <i className="ti ti-clock-stop nav-text-icon" />
                  <span className="nav-text">{loading ? 'Processing...' : 'Check Out'}</span>
                </button>
              ) : null}

              {attendanceStatus && attendanceStatus.check_in && (
                <div className="nav-text text-muted text-center mt-2" style={{ fontSize: '11px' }}>
                  In: {new Date(attendanceStatus.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {attendanceStatus.check_out && (
                    <> &bull; Out: {new Date(attendanceStatus.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="avatar avatar-sm avatar-primary flex-shrink-0">
              <span className="avatar-initials rounded-circle">{initial}</span>
            </span>
            <div className="nav-text overflow-hidden">
              <div className="fw-semibold small text-truncate">{user?.name}</div>
              <div className="text-muted text-truncate" style={{ fontSize: '11px' }}>
                <span className="text-capitalize">{user?.role}</span>
                {' '}&bull;{' '}
                <span className="text-capitalize">
                  {user?.departments ? user.departments.join(', ') : user?.department}
                </span>
              </div>
            </div>
          </div>

          <button onClick={handleLogout} className="btn btn-outline-danger btn-sm w-100 d-flex align-items-center justify-content-center gap-2">
            <i className="ti ti-logout" />
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </aside>

      <main
        id="content"
        className={`content py-10 px-2 px-sm-3 ${sidebarCollapsed ? 'full' : ''}`}
      >
        {children}
      </main>

      {/* Task Modal for Check Out */}
      {showTaskModal && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Check Out</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowTaskModal(false);
                      setTasks('');
                    }}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-3">Please describe the tasks you completed today:</p>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={tasks}
                    onChange={(e) => setTasks(e.target.value)}
                    placeholder="E.g., Completed project documentation, attended team meetings, fixed bug #123..."
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowTaskModal(false);
                      setTasks('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleCheckOut}
                    disabled={loading || !tasks.trim()}
                  >
                    {loading ? 'Checking Out...' : 'Check Out'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Layout;
